import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertTriangle, FileSearch, FileText, ListChecks, ShieldCheck,
  BookOpen, ScrollText, Stethoscope, Gavel, FileEdit, Download,
} from "lucide-react";
import PkiNbaFragebogen from "@/components/pflege-ki/PkiNbaFragebogen";
import PkiNbaSummary from "@/components/pflege-ki/PkiNbaSummary";
import PkiNbaGutachten from "@/components/pflege-ki/PkiNbaGutachten";
import PkiHalluzWertungBanner from "@/components/pflege-ki/PkiHalluzWertungBanner";
import PkiBeobachtungBanner from "@/components/pflege-ki/PkiBeobachtungBanner";
import PkiModulTrennungBanner from "@/components/pflege-ki/PkiModulTrennungBanner";
import PkiKonsistenzWarning from "@/components/pflege-ki/PkiKonsistenzWarning";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = [
  { id: "pflegegrad",   label: "Pflegegrad-Einschätzung",   icon: Stethoscope,
    desc: "Strukturierter Hilfebedarf je NBA-Modul · ohne Pflegegrad-Empfehlung." },
  { id: "hoeherstufung", label: "Höherstufungsbegründung", icon: ScrollText,
    desc: "Sachlicher Brief an die Pflegekasse mit modulbezogener Begründung. Konsistenz-Check NBA verpflichtend." },
  { id: "widerspruch",  label: "Widerspruchsentwurf",       icon: Gavel,
    desc: "Widerspruch gegen einen Pflegegradbescheid · mit pflegefachlicher Begründung. Konsistenz-Check NBA verpflichtend." },
  { id: "leistungsanpassung", label: "Antrag auf Leistungsanpassung", icon: FileEdit,
    desc: "Anpassung von Pflegesachleistungen, Entlastungsbetrag, Hilfsmitteln — ohne Höherstufung des Pflegegrads." },
  { id: "md",           label: "MD-Vorbereitung",           icon: ShieldCheck,
    desc: "Checkliste & Stichpunkte für das Gespräch mit dem MD-Gutachter." },
];

const PRIO_BADGE = "inline-flex h-5 px-2 items-center rounded-full text-[10px] uppercase tracking-[0.18em] font-bold";

// Reihenfolge der Job-Stages für die Live-Anzeige (muss zum Backend passen)
const STAGE_ORDER = [
  "Auftrag angenommen",
  "Eingaben werden geprüft",
  "NBA-Module werden ausgewertet",
  "Bericht wird erstellt",
  "Bericht wird geprüft",
  "Ergebnis ist fertig",
];


/**
 * Baut aus dem strukturierten Pflege-KI-Input einen NBA-tauglichen Freitext.
 * Wird verwendet, um beim Wechsel auf den NBA-Tab automatisch die Eingabe zu füllen,
 * wenn ein Testkunde geladen wurde – sonst kann die NBA-Analyse nicht laufen.
 */
const buildNbaTextFromInput = (input) => {
  if (!input || typeof input !== "object") return "";
  const lines = [];
  const k = input.kundendaten || {};
  if (k.alter || k.pflegegrad || k.wohnsituation) {
    const head = [
      k.alter ? `${k.alter} J.` : null,
      k.pflegegrad ? `PG ${k.pflegegrad}` : null,
      k.wohnsituation,
    ].filter(Boolean).join(", ");
    if (head) lines.push(head + ".");
  }
  if (k.angehoerige) lines.push(`Angehörige: ${k.angehoerige}.`);
  if (k.versorgungsziel) lines.push(`Versorgungsziel: ${k.versorgungsziel}.`);
  const ps = input.pflegesituation || {};
  Object.entries(ps).forEach(([key, v]) => { if (v) lines.push(`${key}: ${v}.`); });
  const r = input.risiken || {};
  Object.entries(r).forEach(([key, v]) => { if (v) lines.push(`Risiko ${key}: ${v}.`); });
  const bp = input.behandlungspflege || {};
  Object.entries(bp).forEach(([key, v]) => { if (v) lines.push(`Behandlungspflege ${key}: ${v}.`); });
  const ft = input.freitext || {};
  Object.entries(ft).forEach(([key, v]) => { if (v) lines.push(`${key}: ${v}.`); });
  if (input.rohbericht) lines.push(input.rohbericht);
  return lines.join(" ");
};

/** NBA-Analyse & Pflegegrad-Assistent — Tab innerhalb der Pflege-KI-Seite */
export default function PkiNbaTab({ customerLabel, kundenInput, initialSis, initialPlan }) {
  const [view, setView] = useState("freitext");  // freitext | fragebogen
  const [eingabeText, setEingabeText] = useState("");
  const [sisText, setSisText] = useState("");
  const [planText, setPlanText] = useState("");
  const [aktPg, setAktPg] = useState("");
  const [zielPg, setZielPg] = useState("");
  const [narrativeMode, setNarrativeMode] = useState(false);  // Iter 27.21 · Opt-In Stil-Switch
  const [modules, setModules] = useState([]);
  const [analyse, setAnalyse] = useState(null);
  const [reports, setReports] = useState({});  // { typ: {text, kb_sources, generated_at} }
  const [busyAnalyse, setBusyAnalyse] = useState(false);
  // Gutachten · sichtbarer fachlicher Fließtext-Output (LLM-generiert, JSON-strukturiert)
  const [gutachten, setGutachten] = useState(null);           // { gutachten, generated_at, ... }
  const [busyGutachten, setBusyGutachten] = useState(false);
  const [gutachtenError, setGutachtenError] = useState(null);  // String oder null
  // Konsistenz-Warnung · wird angezeigt, wenn Backend 412 mit konsistenz-Detail liefert
  const [konsistenzWarning, setKonsistenzWarning] = useState(null);
  // { konsistenz: {...}, targetBriefTyp: 'hoeherstufung' | 'widerspruch' }
  // jobs[typ] = { job_id, status, stage, progress, error, started_at }
  const [reportJobs, setReportJobs] = useState({});
  const pollTimersRef = useRef({});  // typ → setTimeout-id
  const [analyseId, setAnalyseId] = useState(null);

  const REPORT_JOBS_LS_KEY = "pki_nba_report_jobs_v1";

  // Auto-Befüllung: sobald ein Testkunde geladen wurde, Eingabe-Text vorbefüllen,
  // damit die NBA-Analyse ohne manuelles Tippen lauffähig ist. SIS/Plan-Text werden
  // ebenfalls vorbefüllt, sofern bereits ein Entwurf existiert. Wird pro
  // customerLabel nur einmal ausgeführt (ref-guarded).
  const lastAutofilledLabelRef = useRef(null);
  useEffect(() => {
    if (lastAutofilledLabelRef.current === customerLabel) return;
    lastAutofilledLabelRef.current = customerLabel;
    const generated = buildNbaTextFromInput(kundenInput);
    if (generated) setEingabeText((prev) => (prev.trim() ? prev : generated));
    if (initialSis) setSisText((prev) => (prev.trim() ? prev : initialSis));
    if (initialPlan) setPlanText((prev) => (prev.trim() ? prev : initialPlan));
  }, [customerLabel, kundenInput, initialSis, initialPlan]);

  // Hydrate jobs aus localStorage nach Refresh
  useEffect(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(REPORT_JOBS_LS_KEY) || "{}"); } catch { /* leise */ }
    if (!stored || typeof stored !== "object") return;
    Object.entries(stored).forEach(([typ, st]) => {
      if (st?.job_id) {
        setReportJobs((p) => ({ ...p, [typ]: { ...st, hydrating: true } }));
        // Sofort einmal polling, dann ggf. Loop starten
        pollReportJob(typ, st.job_id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistiere aktive Jobs in localStorage
  useEffect(() => {
    const slim = {};
    Object.entries(reportJobs).forEach(([typ, st]) => {
      if (st?.job_id && (st.status === "queued" || st.status === "running")) {
        slim[typ] = { job_id: st.job_id, status: st.status, stage: st.stage, started_at: st.started_at };
      }
    });
    try { localStorage.setItem(REPORT_JOBS_LS_KEY, JSON.stringify(slim)); } catch { /* leise */ }
  }, [reportJobs]);

  const clearPollTimer = (typ) => {
    if (pollTimersRef.current[typ]) {
      clearTimeout(pollTimersRef.current[typ]);
      delete pollTimersRef.current[typ];
    }
  };

  // Eine Polling-Iteration · plant sich selbst neu solange queued/running
  const pollReportJob = async (typ, jobId) => {
    try {
      const r = await axios.get(`${API}/pflege-ki/nba/jobs/${jobId}`, { withCredentials: true });
      const j = r.data || {};
      setReportJobs((p) => ({
        ...p,
        [typ]: {
          job_id: jobId,
          status: j.status,
          stage: j.stage,
          progress: typeof j.progress === "number" ? j.progress : (p[typ]?.progress ?? 0),
          error: j.error,
          error_code: j.error_code,
          started_at: p[typ]?.started_at || j.created_at,
        },
      }));
      if (j.status === "done") {
        setReports((p) => ({ ...p, [typ]: j.result }));
        toast.success(`${REPORT_TYPES.find((t) => t.id === typ)?.label} fertig.`);
        clearPollTimer(typ);
        return;
      }
      if (j.status === "error") {
        clearPollTimer(typ);
        return;
      }
      // weiter pollen
      pollTimersRef.current[typ] = setTimeout(() => pollReportJob(typ, jobId), 2000);
    } catch (e) {
      // 404 → job gibt es nicht mehr → wegwerfen
      if (e?.response?.status === 404) {
        setReportJobs((p) => { const n = { ...p }; delete n[typ]; return n; });
        clearPollTimer(typ);
        return;
      }
      // anderer Fehler → in 4 s nochmal versuchen
      pollTimersRef.current[typ] = setTimeout(() => pollReportJob(typ, jobId), 4000);
    }
  };

  const generateReport = async (typ, opts = {}) => {
    if (!eingabeText.trim()) {
      toast.error("Bitte zuerst den Hilfebedarf beschreiben.");
      return;
    }
    // Dedup auf Frontend-Seite: läuft schon ein Job für diesen Typ?
    if (reportJobs[typ]?.status === "queued" || reportJobs[typ]?.status === "running") {
      toast.info("Berichtserstellung läuft bereits. Bitte warten oder Tab wechseln.");
      return;
    }
    setReportJobs((p) => ({
      ...p,
      [typ]: { job_id: null, status: "queued", stage: "Auftrag wird angenommen", progress: 0, started_at: new Date().toISOString() },
    }));
    try {
      const r = await axios.post(`${API}/pflege-ki/nba/report`, {
        bericht_typ: typ,
        eingabe_text: eingabeText,
        sis_text: sisText,
        massnahmenplan_text: planText,
        customer_label: customerLabel || null,
        aktueller_pflegegrad: aktPg ? Number(aktPg) : null,
        gewuenschter_pflegegrad: zielPg ? Number(zielPg) : null,
        konsistenz_override_text: opts.konsistenzOverrideText || null,
        narrative_mode: typ !== "leistungsanpassung" && !!narrativeMode,
      }, { withCredentials: true });
      const jobId = r.data?.job_id;
      const dedup = !!r.data?.dedup;
      setReportJobs((p) => ({
        ...p,
        [typ]: {
          job_id: jobId,
          status: r.data?.status || "queued",
          stage: r.data?.stage || "Auftrag angenommen",
          progress: 0,
          started_at: new Date().toISOString(),
        },
      }));
      if (dedup) toast.info("Dieser Bericht läuft bereits — Fortschritt wird angezeigt.");
      if (jobId) pollReportJob(typ, jobId);
    } catch (e) {
      // Konsistenz-Block/Warn-Fall: Backend liefert 412 mit { code, konsistenz }
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 412 && detail && typeof detail === "object" && detail.konsistenz) {
        setReportJobs((p) => { const n = { ...p }; delete n[typ]; return n; });
        setKonsistenzWarning({
          konsistenz: detail.konsistenz,
          targetBriefTyp: typ,
        });
        return;
      }
      const msg = detail || "Berichtserstellung fehlgeschlagen.";
      setReportJobs((p) => ({
        ...p,
        [typ]: { ...(p[typ] || {}), status: "error", stage: "Auftrag fehlgeschlagen", error: msg },
      }));
      toast.error(typeof msg === "string" ? msg : "Berichtserstellung fehlgeschlagen.");
    }
  };

  const retryReport = (typ) => {
    setReportJobs((p) => { const n = { ...p }; delete n[typ]; return n; });
    clearPollTimer(typ);
    // Sofort neuen Versuch (User-Erwartung „Erneut versuchen" startet einen frischen Lauf)
    setTimeout(() => generateReport(typ), 80);
  };

  // Cleanup beim Unmount
  useEffect(() => () => {
    Object.values(pollTimersRef.current).forEach((id) => clearTimeout(id));
    pollTimersRef.current = {};
  }, []);

  // Lade NBA-Module-Definition
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/nba/modules`, { withCredentials: true });
        setModules(r.data?.modules || []);
      } catch { /* ignored */ }
    })();
  }, []);

  const runAnalyse = async () => {
    if (!eingabeText.trim()) {
      toast.error("Bitte zuerst den Hilfebedarf beschreiben (Eingabetext).");
      return;
    }
    setBusyAnalyse(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba/analyse`, {
        eingabe_text: eingabeText,
        sis_text: sisText,
        massnahmenplan_text: planText,
        customer_label: customerLabel || null,
      }, { withCredentials: true });
      setAnalyse(r.data);
      // Bei neuer Analyse altes Gutachten löschen (sonst widersprüchliche Anzeige)
      setGutachten(null);
      setGutachtenError(null);
      toast.success(`Analyse fertig · ${r.data?.modules_betroffen?.length || 0} Module erkannt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen.");
    } finally {
      setBusyAnalyse(false);
    }
  };

  /**
   * Erzeugt das fachliche NBA-Gutachten (LLM-Fließtext, JSON-strukturiert) und macht
   * es zur Hauptansicht. Die deterministische Analyse bleibt zusätzlich verfügbar
   * (unter "Interne Analyse" eingeklappt).
   */
  const runGutachten = async () => {
    if (!eingabeText.trim()) {
      toast.error("Bitte zuerst den Hilfebedarf beschreiben (Eingabetext).");
      return;
    }
    setBusyGutachten(true);
    setGutachtenError(null);
    try {
      const r = await axios.post(`${API}/pflege-ki/nba/gutachten`, {
        eingabe_text: eingabeText,
        sis_text: sisText,
        massnahmenplan_text: planText,
        customer_label: customerLabel || null,
        aktueller_pflegegrad: aktPg ? Number(aktPg) : null,
        gewuenschter_pflegegrad: zielPg ? Number(zielPg) : null,
      }, { withCredentials: true, timeout: 100000 });
      setGutachten(r.data);
      // Auch die interne Analyse aus dem Gutachten-Response übernehmen, falls
      // der Nutzer das Gutachten direkt ohne vorherige Analyse erzeugt hat
      if (!analyse && r.data?.interne_vorpruefung) {
        const iv = r.data.interne_vorpruefung;
        setAnalyse({
          ok: true,
          checked_at: r.data.generated_at,
          customer_label: r.data.customer_label,
          modules_alle: iv.modules_alle,
          modules_betroffen: iv.modules_betroffen,
          missing_topics: iv.missing_topics,
          missing_topics_fragen: iv.missing_topics_fragen,
          pflegegrad_tendenz: iv.pflegegrad_tendenz_heuristik,
          moegl_hilfebedarf: iv.moegl_hilfebedarf,
          md_hinweise: [],
          kb_matches: iv.kb_matches,
          contradictions: [],
          disclaimer: r.data.disclaimer,
        });
      }
      toast.success("Fachliches Gutachten erstellt.");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Gutachten-Erstellung fehlgeschlagen.";
      setGutachtenError(typeof detail === "string" ? detail : "Gutachten-Erstellung fehlgeschlagen.");
      toast.error(typeof detail === "string" ? detail : "Gutachten-Erstellung fehlgeschlagen.");
    } finally {
      setBusyGutachten(false);
    }
  };

  const saveAnalyse = async () => {
    try {
      const r = await axios.post(`${API}/pflege-ki/nba/save`, {
        id: analyseId,
        customer_label: customerLabel || null,
        eingabe_text: eingabeText,
        sis_text: sisText,
        massnahmenplan_text: planText,
        analyse_ergebnis: analyse,
        berichte: reports,
      }, { withCredentials: true });
      setAnalyseId(r.data.id);
      toast.success("Analyse gespeichert.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  };

  const copyReport = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("In Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  /**
   * Lädt einen Brief als Markdown-Datei herunter (für E-Mail-Anhang, Akte etc.).
   * Filename mit Brief-Typ, Kundenlabel und ISO-Datum.
   */
  const downloadReportMd = (text, typ) => {
    try {
      const cl = (customerLabel || "Anonym").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40);
      const d = new Date().toISOString().slice(0, 10);
      const fname = `NBA-${typ}-${cl}-${d}.md`;
      const blob = new Blob([text || ""], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Brief gespeichert als ${fname}`);
    } catch (e) {
      toast.error("Download fehlgeschlagen.");
    }
  };

  /** Öffnet einen druckbaren PDF-View des Briefs in einem neuen Tab (Browser-Print). */
  const printReportPdf = (text, typ) => {
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Bitte Popup-Blocker für diese Seite deaktivieren."); return; }
    const safe = (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8" />
      <title>${typ} · ${customerLabel || "Anonym"}</title>
      <style>
        body { font-family: Georgia, 'Times New Roman', serif; max-width: 720px; margin: 2.5cm auto; line-height: 1.55; color: #333; }
        h1 { font-size: 22pt; border-bottom: 1px solid #bbb; padding-bottom: 6px; }
        h2 { font-size: 14pt; margin-top: 1.6em; color: #5a4a2a; }
        pre { white-space: pre-wrap; word-wrap: break-word; font: inherit; }
        .meta { color: #888; font-size: 10pt; border-top: 1px dashed #ccc; padding-top: 8px; margin-top: 2em; }
        @media print { body { margin: 1.5cm; } }
      </style></head><body>
      <pre>${safe}</pre>
      <p class="meta">Erstellt mit Dickies-Helden · Pflege-KI · ${new Date().toLocaleString("de-DE")} · Pflegefachlicher Entwurf, ersetzt keine MD-Begutachtung.</p>
      <script>setTimeout(function(){window.print();}, 400);</script>
      </body></html>`);
    w.document.close();
  };

  /**
   * Baut aus der NBA-Analyse einen kopierfreundlichen Klartext zum Einfügen in
   * Pflegedokumentation / Pflegebericht / Mail. Enthält Pflegegrad-Tendenz,
   * Module mit Hinweisen, Widersprüche und fehlende Folgefragen.
   *
   * Schema-Stand (Backend pflege_ki_nba.py):
   *   pflegegrad_tendenz: { tendenz_label, ampel, schwere_treffer[], leichte_treffer[], disclaimer }
   *   modules_alle: [{ nummer, name, betroffen, matched_keywords[], typische_aspekte[] }]
   *   contradictions: [{ bereich, warnung, sis_hits[], plan_hits[] }]
   *   missing_topics_fragen: [{ topic, frage }]
   */
  const buildAnalyseClipboardText = (a) => {
    if (!a) return "";
    const lines = [];
    lines.push("NBA-Analyse (Pflege-KI)");
    lines.push("=".repeat(50));

    // Pflegegrad-Tendenz (Objekt!)
    const t = a.pflegegrad_tendenz || {};
    if (t.tendenz_label) {
      const ampel = t.ampel ? ` (${t.ampel})` : "";
      lines.push(`Pflegegrad-Tendenz: ${t.tendenz_label}${ampel}`);
    }
    if (a.moegl_hilfebedarf) {
      lines.push(`Möglicher Hilfebedarf: ${a.moegl_hilfebedarf}`);
    }
    if (Array.isArray(t.schwere_treffer) && t.schwere_treffer.length) {
      lines.push(`Schwere Hinweise: ${t.schwere_treffer.join(", ")}`);
    }
    if (Array.isArray(t.leichte_treffer) && t.leichte_treffer.length) {
      lines.push(`Leichte Hinweise: ${t.leichte_treffer.join(", ")}`);
    }
    lines.push("");

    // Module — echtes Schema { nummer, name, betroffen, matched_keywords }
    if (Array.isArray(a.modules_alle) && a.modules_alle.length) {
      lines.push("NBA-Module:");
      a.modules_alle.forEach((m) => {
        const mark = m.betroffen ? "✓" : "—";
        const num = m.nummer ? `Modul ${m.nummer}` : "";
        const name = m.name || "";
        lines.push(`  ${mark} ${num} ${name}`.trimEnd());
        if (m.betroffen && Array.isArray(m.matched_keywords) && m.matched_keywords.length) {
          lines.push(`     Treffer: ${m.matched_keywords.join(", ")}`);
        }
        if (!m.betroffen && Array.isArray(m.typische_aspekte) && m.typische_aspekte.length) {
          lines.push(`     (in den Eingaben nicht erkennbar · typische Aspekte: ${m.typische_aspekte.slice(0, 3).join(", ")})`);
        }
      });
      lines.push("");
    }

    if (Array.isArray(a.contradictions) && a.contradictions.length) {
      lines.push(`Widersprüche SIS ↔ Maßnahmenplan (${a.contradictions.length}):`);
      a.contradictions.forEach((c, i) => {
        lines.push(`  ${i + 1}. ${c.bereich}: ${c.warnung}`);
        if (c.sis_hits?.length || c.plan_hits?.length) {
          lines.push(`     SIS: ${(c.sis_hits || []).join(", ")} · Plan: ${(c.plan_hits || []).join(", ")}`);
        }
      });
      lines.push("");
    }

    if (Array.isArray(a.missing_topics_fragen) && a.missing_topics_fragen.length) {
      lines.push("Offene Folgefragen:");
      a.missing_topics_fragen.forEach((f) => {
        lines.push(`  - [${f.topic}] ${f.frage}`);
      });
      lines.push("");
    }

    if (t.disclaimer) lines.push(t.disclaimer);
    else if (a.disclaimer) lines.push(a.disclaimer);

    return lines.join("\n");
  };

  return (
    <div className="space-y-5" data-testid="pki-nba-tab">
      {/* SUB-NAV: Freitext-Analyse vs. Fragebogen+Radar */}
      <div className="rounded-2xl bg-cream-100 p-1.5 inline-flex gap-1" data-testid="nba-mode-toggle">
        <button onClick={() => setView("freitext")}
          data-testid="nba-mode-freitext"
          className={`px-3.5 h-9 rounded-xl text-[12.5px] font-semibold transition-colors ${
            view === "freitext" ? "bg-sand-800 text-cream-50" : "text-sand-700 hover:text-sand-800"
          }`}>
          Freitext-Analyse
        </button>
        <button onClick={() => setView("fragebogen")}
          data-testid="nba-mode-fragebogen"
          className={`px-3.5 h-9 rounded-xl text-[12.5px] font-semibold transition-colors ${
            view === "fragebogen" ? "bg-sand-800 text-cream-50" : "text-sand-700 hover:text-sand-800"
          }`}>
          Pflegegrad-Radar &amp; Booster
        </button>
      </div>

      {view === "fragebogen" && <PkiNbaFragebogen customerLabel={customerLabel} />}
      {view === "freitext" && (<>
      {/* HEADER + DISCLAIMER */}
      <div className="rounded-2xl border border-cream-300 bg-cream-50 p-5">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
          NBA-Analyse · Pflegegrad-Assistent
        </div>
        <h3 className="font-display text-xl text-sand-800 mt-1">
          Hilfebedarf nach 6 NBA-Modulen strukturieren
        </h3>
        <p className="mt-2 text-[13px] text-sand-600 leading-snug max-w-3xl">
          Erkennt deterministisch betroffene NBA-Module, fehlende Informationen und Widersprüche
          gegen SIS & Maßnahmenplan. Optional schreibt Claude 4.5 daraus einen Bericht für
          Pflegegrad-Einschätzung, Höherstufung, Widerspruch oder MD-Vorbereitung.
        </p>
        <div className="mt-3 rounded-xl bg-peach-50 border border-terra/30 px-3 py-2 text-[12px] text-sand-700 leading-snug">
          <strong className="text-brand-darkred">Wichtig:</strong> Keine automatische
          Pflegegrad-Entscheidung — nur fachliche Unterstützung. Jede Empfehlung muss
          pflegefachlich geprüft und freigegeben werden.
        </div>
      </div>

      {/* EINGABE */}
      <div className="rounded-2xl border border-cream-300 bg-white p-5 space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
            Beschreibung Hilfebedarf · was wird in welcher Häufigkeit benötigt?
          </label>
          <textarea
            rows={5}
            value={eingabeText}
            onChange={(e) => setEingabeText(e.target.value)}
            placeholder='z. B. "Kundin 84 J., Sturz vor 4 Wochen, unsicherer Gang, benötigt Hilfe beim Duschen und Ankleiden, vergisst Medikamente, Tochter ist überlastet …"'
            data-testid="nba-eingabe-text"
            className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra"
          />
          {customerLabel && (
            <p className="mt-1 text-[11px] text-sand-500 italic">
              Auto-befüllt aus „{customerLabel}“. Inhalte können vor der Analyse angepasst werden.
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              SIS-Text (optional · für Widerspruchsprüfung)
            </label>
            <textarea
              rows={4}
              value={sisText}
              onChange={(e) => setSisText(e.target.value)}
              data-testid="nba-sis-text"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px] text-sand-800 font-mono focus:outline-none focus:border-terra"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
              Maßnahmenplan (optional · für Widerspruchsprüfung)
            </label>
            <textarea
              rows={4}
              value={planText}
              onChange={(e) => setPlanText(e.target.value)}
              data-testid="nba-plan-text"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[12.5px] text-sand-800 font-mono focus:outline-none focus:border-terra"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
              Aktueller PG
            </label>
            <input
              type="number"
              min={0}
              max={5}
              value={aktPg}
              onChange={(e) => setAktPg(e.target.value)}
              data-testid="nba-akt-pg"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">
              Gewünschter PG
            </label>
            <input
              type="number"
              min={0}
              max={5}
              value={zielPg}
              onChange={(e) => setZielPg(e.target.value)}
              data-testid="nba-ziel-pg"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[14px] text-sand-800 focus:outline-none focus:border-terra"
            />
          </div>
          <div className="col-span-2 md:col-span-2 flex items-end justify-end gap-2 flex-wrap">
            {analyse && (
              <Button onClick={saveAnalyse} variant="outline" size="sm"
                data-testid="nba-save"
                className="h-10 rounded-full border-clay">
                Analyse speichern
              </Button>
            )}
            <Button
              onClick={runAnalyse}
              disabled={busyAnalyse}
              variant="outline"
              data-testid="nba-run-analyse"
              className="h-10 rounded-full border-sage-dark/40 text-sage-dark hover:bg-sage-dark/5 px-5">
              <FileSearch className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {busyAnalyse ? "Analysiere …" : "Vorprüfung starten"}
            </Button>
            <Button
              onClick={runGutachten}
              disabled={busyGutachten}
              data-testid="nba-run-gutachten"
              className="h-10 rounded-full bg-terra hover:bg-terra/90 text-white px-5">
              <Stethoscope className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {busyGutachten ? "Gutachten wird erstellt …" : "NBA-Gutachten erstellen"}
            </Button>
          </div>
          {busyGutachten && (
            <div
              data-testid="nba-gutachten-progress-note"
              className="col-span-2 md:col-span-4 -mt-2 flex items-center justify-end gap-2 text-[12px] text-clay-dark italic"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-terra animate-pulse" />
              Komplexe Fälle (Demenz, mehrere betroffene Module) benötigen bis zu 90&nbsp;Sekunden.
            </div>
          )}
        </div>
      </div>

      {/* GUTACHTEN-FEHLER (LLM-Budget leer etc.) */}
      {gutachtenError && !gutachten && (
        <div
          data-testid="nba-gutachten-error"
          className="rounded-2xl border border-brand-darkred/40 bg-brand-darkred/5 p-5"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brand-darkred mb-1.5">
            Gutachten konnte nicht erstellt werden
          </div>
          <p className="text-[13px] text-sand-700 leading-relaxed">
            {gutachtenError}
          </p>
          {analyse && (
            <p className="mt-2 text-[12px] text-sand-600 italic">
              Die interne Vorprüfung weiter unten bleibt verfügbar.
            </p>
          )}
        </div>
      )}

      {/* HAUPT-ANSICHT · Fachliches NBA-Gutachten (Fließtext, JSON-strukturiert) */}
      {gutachten?.gutachten && (
        <>
          <PkiHalluzWertungBanner halluz={gutachten.halluzinationscheck} />
          <PkiBeobachtungBanner data={gutachten.beobachtung_check} />
          <PkiModulTrennungBanner data={gutachten.modul_trennung_check} />
          <PkiNbaGutachten
            gutachten={gutachten.gutachten}
            generatedAt={gutachten.generated_at}
            customerLabel={gutachten.customer_label}
          />
        </>
      )}

      {/* INTERNE ANALYSE · deterministische Vorprüfung (eingeklappt unter dem Gutachten) */}
      {analyse && (
        <details
          data-testid="nba-interne-analyse"
          className="rounded-2xl border border-cream-300 bg-cream-50/60 overflow-hidden"
        >
          <summary className="cursor-pointer px-5 py-4 flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] font-bold text-clay-dark hover:bg-cream-100 transition-colors">
            <FileSearch className="h-4 w-4" strokeWidth={2} />
            Interne Analyse · technische Vorprüfung
            <span className="ml-auto text-[10.5px] text-sand-500 normal-case tracking-normal italic font-normal">
              (zum Aufklappen klicken · deterministische Auswertung für Halluzinationsschutz)
            </span>
          </summary>
          <div className="p-5 pt-2 border-t border-cream-200 space-y-4" data-testid="nba-analyse-result">

          {/* Copy-NBA-Analyse als Klartext (kopierbar für Pflegedoku) */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="nba-analyse-copy"
              onClick={() => copyReport(buildAnalyseClipboardText(analyse))}
              className="text-[12px] h-8"
            >
              NBA-Analyse kopieren
            </Button>
          </div>

          {/* Pflegegrad-Tendenz + alle 6 Module + fehlende Folgefragen (Shared) */}
          <PkiNbaSummary
            tendenz={analyse.pflegegrad_tendenz}
            modulesAll={analyse.modules_alle}
            missingFragen={analyse.missing_topics_fragen}
            testIdPrefix="nba"
            onMissingClick={(frage, topic) => {
              setEingabeText((prev) => (prev ? prev.trimEnd() + "\n\n" : "") + `[Folgefrage] ${frage}`);
              toast.success(`„${topic}" als Folgefrage angehängt.`);
            }}
          />

          {/* Heuristische Einschätzung + Hinweis */}
          <div className="rounded-2xl border border-sage-dark/30 bg-sage-dark/5 p-5">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-sage-dark">
              Heuristische Einschätzung
            </div>
            <div className="mt-1 text-[15px] font-semibold text-sand-800">
              Möglicher Hilfebedarf: <span className="text-sage-dark">{analyse.moegl_hilfebedarf}</span>
            </div>
            <p className="mt-1.5 text-[12px] text-sand-600 italic">{analyse.disclaimer}</p>
          </div>

          {/* Widersprüche */}
          {analyse.contradictions?.length > 0 && (
            <div className="rounded-2xl border border-brand-darkred/40 bg-brand-darkred/5 p-5"
              data-testid="nba-contradictions">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-brand-darkred" strokeWidth={2} />
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
                  Widersprüche SIS ↔ Maßnahmenplan ({analyse.contradictions.length})
                </div>
              </div>
              <ul className="space-y-2">
                {analyse.contradictions.map((c, i) => (
                  <li key={i} className="rounded-xl border border-brand-darkred/30 bg-white p-3">
                    <div className="text-[12.5px] font-semibold text-sand-800">{c.bereich}</div>
                    <div className="text-[12px] text-sand-700 leading-snug mt-0.5">{c.warnung}</div>
                    <div className="mt-1 text-[11px] text-sand-500">
                      SIS-Treffer: <em>{(c.sis_hits || []).join(", ")}</em> · Plan-Treffer: <em>{(c.plan_hits || []).join(", ")}</em>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* MD-Hinweise & KB-Quellen */}
          {analyse.md_hinweise?.length > 0 && (
            <div className="rounded-2xl border border-cream-300 bg-white p-5"
              data-testid="nba-md-hinweise">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-clay-dark" strokeWidth={2} />
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                  MD-Hinweise aus der Wissensbibliothek ({analyse.md_hinweise.length})
                </div>
              </div>
              <ul className="space-y-1.5 text-[12.5px] text-sand-700 leading-relaxed">
                {analyse.md_hinweise.map((h, i) => (
                  <li key={i} className="flex gap-2"><span className="text-sage-dark">→</span><span>{h}</span></li>
                ))}
              </ul>
              {analyse.kb_matches?.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-[11.5px] text-clay-dark uppercase tracking-[0.22em] font-semibold">
                    Quellen ({analyse.kb_matches.length} KB-Regeln)
                  </summary>
                  <div className="mt-2 text-[11.5px] text-sand-600 font-mono leading-relaxed">
                    {analyse.kb_matches.map((m) => (
                      <div key={m.rule_id}>· {m.bereich} ({m.risiko}) — KB:{(m.rule_id || "").slice(0, 8)}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          </div>
        </details>
      )}

      {/* KONSISTENZ-WARNUNG · erscheint, wenn Backend 412 mit konsistenz-Detail meldet */}
      {konsistenzWarning && (
        <PkiKonsistenzWarning
          konsistenz={{ ...konsistenzWarning.konsistenz, bericht_typ: konsistenzWarning.targetBriefTyp }}
          onAlternative={() => {
            const w = konsistenzWarning;
            setKonsistenzWarning(null);
            generateReport("leistungsanpassung");
            toast.info("Alternative gewählt: Antrag auf Leistungsanpassung wird erstellt.");
            void w;
          }}
          onProceedWithOverride={async (overrideText) => {
            const targetTyp = konsistenzWarning.targetBriefTyp;
            setKonsistenzWarning(null);
            await generateReport(targetTyp, { konsistenzOverrideText: overrideText });
          }}
          onCancel={() => setKonsistenzWarning(null)}
        />
      )}

      {/* BERICHTSERSTELLUNG */}
      {analyse && (
        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-5"
          data-testid="nba-reports-section">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-1">
            Berichts-Erstellung (Claude 4.5)
          </div>
          <p className="text-[12.5px] text-sand-600 leading-snug mb-4">
            Erstellt einen formalen Entwurf basierend auf der deterministischen Analyse plus den
            gematchten Quellen aus der Wissensbibliothek. Keine Pflegegrad-Entscheidung.
          </p>

          {/* Stil-Toggle: Sachlich (Default) ↔ Narrativ (Opt-In) · Iter 27.21 */}
          <div
            data-testid="nba-stil-toggle"
            className="mb-4 flex items-stretch gap-0 rounded-xl border border-cream-300 bg-white p-1 max-w-md"
          >
            <button
              type="button"
              onClick={() => setNarrativeMode(false)}
              data-testid="nba-stil-sachlich"
              className={`flex-1 px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-colors ${
                !narrativeMode ? "bg-clay-dark text-white shadow-sm" : "text-sand-600 hover:bg-cream-100"
              }`}
            >
              Sachlich (Standard)
            </button>
            <button
              type="button"
              onClick={() => setNarrativeMode(true)}
              data-testid="nba-stil-narrativ"
              className={`flex-1 px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-colors ${
                narrativeMode ? "bg-clay-dark text-white shadow-sm" : "text-sand-600 hover:bg-cream-100"
              }`}
            >
              Narrativ (Fließtext)
            </button>
          </div>
          <p className="text-[11.5px] text-sand-500 leading-snug mb-4 -mt-2 max-w-2xl">
            {narrativeMode
              ? `Narrativer Stil: Brief im Fließtext mit konkreten Alltagsbeobachtungen — wie von einer erfahrenen Pflegefachkraft geschrieben. Selbe Fakten, nur die Darstellung ändert sich. Nicht verfügbar für „Antrag auf Leistungsanpassung" (bleibt sachlich).`
              : "Sachlich-strukturierter Stil mit klaren Abschnitten. Empfohlen für formale Anträge."}
          </p>

          <div className="grid md:grid-cols-2 gap-3">
            {REPORT_TYPES.map((t) => {
              const Icon = t.icon;
              const has = !!reports[t.id];
              const job = reportJobs[t.id];
              const busy = job?.status === "queued" || job?.status === "running";
              const errored = job?.status === "error";
              return (
                <div
                  key={t.id}
                  data-testid={`nba-report-card-${t.id}`}
                  className={`rounded-2xl border p-4 transition-colors ${
                    has ? "border-sage-dark/40 bg-white" :
                    errored ? "border-brand-darkred/40 bg-peach-50" :
                    "border-cream-300 bg-white"
                  }`}>
                  <button
                    type="button"
                    onClick={() => !busy && generateReport(t.id)}
                    disabled={busy}
                    data-testid={`nba-report-btn-${t.id}`}
                    className="w-full text-left disabled:opacity-60"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4 text-clay-dark" strokeWidth={2} />
                      <span className="text-[13px] font-bold text-sand-800">{t.label}</span>
                      {has    && <span className={`${PRIO_BADGE} bg-sage-dark text-white ml-auto`}>fertig</span>}
                      {busy   && <span className={`${PRIO_BADGE} bg-saffron text-clay-dark ml-auto`}>läuft</span>}
                      {errored&& <span className={`${PRIO_BADGE} bg-brand-darkred text-white ml-auto`}>Fehler</span>}
                    </div>
                    <div className="text-[11.5px] text-sand-600 leading-snug">
                      {t.desc}
                    </div>
                  </button>

                  {/* Live-Fortschritt */}
                  {busy && (
                    <div className="mt-3" data-testid={`nba-report-progress-${t.id}`}>
                      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-clay-dark">
                        {job.stage || "in Bearbeitung"} · {job.progress ?? 0}%
                      </div>
                      <div className="h-1.5 mt-1 rounded-full bg-cream-200 overflow-hidden">
                        <div className="h-full bg-brand-darkred transition-all"
                             style={{ width: `${Math.max(4, job.progress ?? 8)}%` }} />
                      </div>
                      <ul className="mt-2 space-y-0.5 text-[10.5px] text-sand-600">
                        {[
                          "Auftrag angenommen",
                          "Eingaben werden geprüft",
                          "NBA-Module werden ausgewertet",
                          "Bericht wird erstellt",
                          "Bericht wird geprüft",
                          "Ergebnis ist fertig",
                        ].map((s) => {
                          const reached = STAGE_ORDER.indexOf(job.stage) >= STAGE_ORDER.indexOf(s);
                          return (
                            <li key={s} className={`flex items-center gap-1.5 ${reached ? "text-sage-dark" : "text-sand-400"}`}>
                              <span className={`inline-block h-1.5 w-1.5 rounded-full ${reached ? "bg-sage-dark" : "bg-cream-300"}`} aria-hidden="true" />
                              {s}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Fehler + Retry */}
                  {errored && (
                    <div className="mt-3" data-testid={`nba-report-error-${t.id}`}>
                      <div className="text-[11.5px] text-brand-darkred font-semibold mb-2">
                        {job.error || "Erstellung fehlgeschlagen."}
                      </div>
                      <Button
                        onClick={() => retryReport(t.id)}
                        data-testid={`nba-report-retry-${t.id}`}
                        className="h-8 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white font-semibold px-4 text-[12px]"
                      >
                        Erneut versuchen
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FERTIGE BERICHTE */}
      {REPORT_TYPES.filter((t) => reports[t.id]).map((t) => {
        const data = reports[t.id];
        return (
          <div key={t.id} className="rounded-2xl border border-cream-300 bg-white p-5"
            data-testid={`nba-report-output-${t.id}`}>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-clay-dark" strokeWidth={2} />
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                  {data.bericht_label || t.label}
                </div>
              </div>
              <Button onClick={() => copyReport(data.text)} variant="outline" size="sm"
                data-testid={`nba-report-copy-${t.id}`}
                className="h-9 rounded-full border-clay">
                Text kopieren
              </Button>
              <Button onClick={() => downloadReportMd(data.text, t.id)} variant="outline" size="sm"
                data-testid={`nba-report-md-${t.id}`}
                className="h-9 rounded-full border-clay">
                <Download className="h-3.5 w-3.5 mr-1" /> .md
              </Button>
              <Button onClick={() => printReportPdf(data.text, t.id)} size="sm"
                data-testid={`nba-report-print-${t.id}`}
                className="h-9 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50">
                <FileText className="h-3.5 w-3.5 mr-1" /> Druck/PDF
              </Button>
            </div>
            <textarea
              value={data.text}
              onChange={(e) => setReports((p) => ({ ...p, [t.id]: { ...data, text: e.target.value } }))}
              rows={Math.min(40, Math.max(12, (data.text || "").split("\n").length + 2))}
              data-testid={`nba-report-text-${t.id}`}
              className="w-full rounded-xl border border-cream-200 bg-cream-50 px-4 py-3 text-[14px] leading-relaxed text-sand-800 font-mono focus:outline-none focus:border-terra"
            />
            {data.kb_sources?.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-[11.5px] text-clay-dark uppercase tracking-[0.22em] font-semibold">
                  Quellen ({data.kb_sources.length} KB-Regeln)
                </summary>
                <div className="mt-2 text-[11.5px] text-sand-600 font-mono leading-relaxed">
                  {data.kb_sources.map((s) => (
                    <div key={s.rule_id}>· {s.bereich} ({s.risiko}) — KB:{(s.rule_id || "").slice(0, 8)}</div>
                  ))}
                </div>
              </details>
            )}
            <div className="mt-3 text-[11.5px] text-brand-darkred font-semibold">
              {data.disclaimer || "Entwurf · pflegefachliche Freigabe erforderlich."}
            </div>
          </div>
        );
      })}
      </>)}
    </div>
  );
}
